/**
 * useStripeConnect Hook
 *
 * Checks if the current user has a connected Stripe account.
 * Note: If the stripe_connect_accounts table doesn't exist, this hook
 * will gracefully return as if no account exists without making repeated requests.
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

interface StripeConnectAccount {
  id: string;
  stripe_account_id: string;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
  onboarding_complete: boolean;
}

interface UseStripeConnectResult {
  /** The connected Stripe account, if any */
  account: StripeConnectAccount | null;
  /** Whether the account is fully set up and can accept payments */
  isConnected: boolean;
  /** Whether the account exists but onboarding is incomplete */
  isPending: boolean;
  /** Loading state */
  loading: boolean;
  /** Refetch the account status */
  refetch: () => Promise<void>;
}

// Module-level flag to track if table doesn't exist (avoid repeated 404s)
let tableDoesNotExist = false;

export function useStripeConnect(): UseStripeConnectResult {
  const [account, setAccount] = useState<StripeConnectAccount | null>(null);
  const [loading, setLoading] = useState(!tableDoesNotExist);

  const fetchAccount = useCallback(async () => {
    // Skip if we've already determined the table doesn't exist
    if (tableDoesNotExist) {
      setAccount(null);
      setLoading(false);
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setAccount(null);
        return;
      }

      const { data, error } = await supabase
        .from("stripe_connect_accounts")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) {
        // PGRST205 = table doesn't exist - cache this to avoid repeated requests
        if (error.code === "PGRST205") {
          tableDoesNotExist = true;
        } else {
          console.error("Error fetching Stripe Connect account:", error);
        }
        setAccount(null);
        return;
      }

      setAccount(data);
    } catch (error) {
      console.error("Error in useStripeConnect:", error);
      setAccount(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccount();
  }, [fetchAccount]);

  const isConnected = !!(
    account?.onboarding_complete &&
    account?.charges_enabled
  );

  const isPending = !!(
    account &&
    !account.onboarding_complete
  );

  return {
    account,
    isConnected,
    isPending,
    loading,
    refetch: fetchAccount,
  };
}
